import React, { useState, useEffect, useCallback } from 'react';
import ScSpinner from '../ScSpinner';
import { fetchVisitTypes, createVisitType, updateVisitType } from '../../studyConfigurationApi';

const DESIGN_OPTIONS = [
  { value: 'Double Blind', label: 'Double Blind' },
  { value: 'Open Label', label: 'Open Label (Unblinded)' },
];

const emptyDraft = () => ({
  visitCode: '',
  visitName: '',
  beforeOnboarding: false,
  studyDesignDesc: 'Double Blind',
  isActive: true,
});

const VisitTypesTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchVisitTypes();
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load visit types.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startAdd = () => {
    setAdding(true);
    setEditingKey(null);
    setDraft(emptyDraft());
    setSuccess(null);
    setError(null);
  };

  const startEdit = (row) => {
    setAdding(false);
    setEditingKey(row.studyVisitTypeNo);
    setDraft({
      visitCode: row.visitCode,
      visitName: row.visitName,
      beforeOnboarding: !!row.beforeOnboarding,
      studyDesignDesc: row.studyDesignDesc || 'Double Blind',
      isActive: row.isActive !== false,
    });
    setSuccess(null);
    setError(null);
  };

  const cancel = () => {
    setAdding(false);
    setEditingKey(null);
    setDraft(null);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (adding) {
        await createVisitType({
          visitCode: draft.visitCode,
          visitName: draft.visitName,
          beforeOnboarding: draft.beforeOnboarding,
          studyDesignDesc: draft.studyDesignDesc,
          isActive: draft.isActive,
        });
        setSuccess('Visit type created.');
      } else {
        await updateVisitType(editingKey, {
          visitCode: draft.visitCode,
          visitName: draft.visitName,
          beforeOnboarding: draft.beforeOnboarding,
          studyDesignDesc: draft.studyDesignDesc,
          isActive: draft.isActive,
        });
        setSuccess('Visit type updated.');
      }
      cancel();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ScSpinner />;

  return (
    <div className="sc-visit-types">
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}
      <p className="sc-empty" style={{ fontStyle: 'normal', marginBottom: '0.75rem' }}>
        Global visit master. Open Label means Unblinded profile. Only one type can be before onboarding.
        Assign types to a project under Project → Project Visit Types; sites inherit on create.
      </p>
      <div className="sc-toolbar">
        <button
          type="button"
          className="sc-btn sc-btn-primary"
          onClick={startAdd}
          disabled={adding || editingKey != null}
        >
          Add visit type
        </button>
      </div>

      {(adding || editingKey != null) && draft && (
        <div className="sc-email-gateway-form" style={{ marginBottom: '1rem' }}>
          <div className="sc-form-row">
            <label>Code</label>
            <input
              value={draft.visitCode}
              disabled={!adding}
              onChange={(e) => setDraft({ ...draft, visitCode: e.target.value.toUpperCase() })}
              maxLength={50}
            />
          </div>
          <div className="sc-form-row">
            <label>Name</label>
            <input
              value={draft.visitName}
              onChange={(e) => setDraft({ ...draft, visitName: e.target.value })}
              maxLength={200}
            />
          </div>
          <div className="sc-form-row">
            <label>Profile side</label>
            <select
              value={draft.studyDesignDesc}
              onChange={(e) => setDraft({ ...draft, studyDesignDesc: e.target.value })}
            >
              {DESIGN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="sc-form-row">
            <label>
              <input
                type="checkbox"
                checked={draft.beforeOnboarding}
                onChange={(e) => setDraft({ ...draft, beforeOnboarding: e.target.checked })}
              />
              {' '}Before onboarding
            </label>
          </div>
          <div className="sc-form-row">
            <label>
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
              {' '}Active
            </label>
          </div>
          <div className="sc-toolbar">
            <button type="button" className="sc-btn sc-btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="sc-btn" onClick={cancel} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="sc-empty">No visit types. Run the Way 2 global seed script or add types here.</p>
      ) : (
        <div className="sc-grid-wrap">
          <table className="sc-grid">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Before onboard</th>
                <th>Profile side</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.studyVisitTypeNo} className={editingKey === r.studyVisitTypeNo ? 'sc-row-editing' : ''}>
                  <td>{r.visitCode}</td>
                  <td>{r.visitName}</td>
                  <td>{r.beforeOnboarding ? 'Yes' : 'No'}</td>
                  <td>{r.studyDesignDesc}</td>
                  <td>{r.isActive ? 'Yes' : 'No'}</td>
                  <td>
                    <button
                      type="button"
                      className="sc-btn sc-btn-link"
                      onClick={() => startEdit(r)}
                      disabled={adding || editingKey != null}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VisitTypesTab;
